import React, { type ReactNode } from "react";
import { AspectRatio, Box, Flex, type SpaceProps } from "@chakra-ui/react";
import Image, { type StaticImageData } from "next/image";
import sunsetImage from "../../public/assets/product-showcase/sunset.png";
import duskImage from "../../public/assets/product-showcase/dusk.png";
import coastalImage from "../../public/assets/product-showcase/coastal.png";
import mustardImage from "../../public/assets/product-showcase/mustard.png";
import poppyImage from "../../public/assets/product-showcase/poppy.png";
import goldImage from "../../public/assets/product-showcase/gold.png";
import { progressTabMediaRatio } from "./tabs";

const dropShadowMd =
  "drop-shadow(0px 34.807px 20.627px rgba(0, 0, 0, 0.02)) drop-shadow(0px 15.47px 15.47px rgba(0, 0, 0, 0.04)) drop-shadow(0px 3.867px 7.735px rgba(0, 0, 0, 0.05))";

export type ProductShowcaseProps = {
  /**
   * The variant determines which blurred background image to use.
   * @default "sunset"
   */
  variant?: "sunset" | "dusk" | "coastal" | "mustard" | "poppy" | "gold";

  /**
   * Product screen in the form of either an image or animation.
   * Displayed on `sm` screens and larger.
   */
  productScreen: ReactNode;

  /**
   * Displayed on the smallest screens.
   */
  mobileProductScreen?: ReactNode;
} & SpaceProps;

export function ProductShowcase({
  variant = "sunset",
  productScreen,
  mobileProductScreen,
  ...props
}: ProductShowcaseProps) {
  let backgroundImage;

  switch (variant) {
    case "dusk": {
      backgroundImage = duskImage;
      break;
    }

    case "coastal": {
      backgroundImage = coastalImage;
      break;
    }

    case "mustard": {
      backgroundImage = mustardImage;
      break;
    }

    case "poppy": {
      backgroundImage = poppyImage;
      break;
    }

    case "gold": {
      backgroundImage = goldImage;
      break;
    }

    default: {
      backgroundImage = sunsetImage;
    }
  }

  return (
    <AspectRatio borderRadius="small" ratio={progressTabMediaRatio} overflow="hidden" w="100%" {...props}>
      <Flex align="end" boxSize="100%" pos="relative">
        <Image
          fill
          sizes="100%"
          alt="Product Showcase"
          placeholder="blur"
          src={backgroundImage}
          style={{ objectFit: "cover" }}
        />

        <Box pos="absolute" bottom={0} left={{ base: "12%", sm: 0 }} top={{ base: "12%", md: "8%" }} right={0}>
          <Box boxSize="100%" mt="auto" ml="auto" mr={{ base: "0", sm: "auto" }} filter={dropShadowMd}>
            <Box display={{ base: "none", sm: "block" }} boxSize="100%" pos="relative">
              {productScreen}
            </Box>
            <Box display={{ base: "block", sm: "none" }} boxSize="100%" pos="relative">
              {mobileProductScreen}
            </Box>
          </Box>
        </Box>
      </Flex>
    </AspectRatio>
  );
}

type ImageProps = {
  isMobile?: boolean;
  isContained?: boolean;
  src: StaticImageData;
  alt: string;
};

/** Helper component to use for the ProductScreen when using a static image. */
export function ProductShowcaseImage({ isMobile, isContained, src, alt }: ImageProps) {
  return (
    <Image
      fill
      sizes="100%"
      placeholder="blur"
      src={src}
      alt={alt}
      style={{
        borderTopLeftRadius: "6px",
        borderTopRightRadius: isMobile ? "none" : "6px",
        objectFit: isContained ? "contain" : "cover",
        objectPosition: isMobile ? "bottom left" : "bottom center",
      }}
    />
  );
}
